/**
 * Base Component
 * Abstract base class for UI components. Consolidates container initialization,
 * translation, CSS class extraction, and lifecycle methods.
 *
 * Subclasses must implement:
 * - getDefaultCssClass(): default CSS class name
 * - getTranslationFallbacks(): fallback translations when UIManager is unavailable
 * - render(): component HTML rendering
 *
 * Optional overrides:
 * - onInitialize(): called after render, for event listeners and store subscriptions
 * - updateUILanguage(): called on language change (default: re-renders)
 * - destroy(): cleanup (default: clears container innerHTML)
 */

import { initializeContainer } from '../core/utilities/dom';
import type { IUIManager, TranslationParams } from '../i18n/ui-manager.types';

export interface BaseComponentOptions {
  cssClass?: string;
  uiManager?: IUIManager;
}

export abstract class BaseComponent {
  protected container: HTMLElement | null = null;
  protected baseCssClass: string;
  protected cssClass: string;
  protected uiManager?: IUIManager;

  constructor(options: BaseComponentOptions = {}) {
    this.cssClass = options.cssClass || this.getDefaultCssClass();
    this.baseCssClass = this.cssClass.split(' ')[0] ?? this.cssClass;
    this.uiManager = options.uiManager;
  }

  /**
   * Initialize the component with a container element or selector.
   */
  initialize(container: HTMLElement | string): void {
    this.container = initializeContainer(
      container,
      `${this.baseCssClass} container not found`
    );
    this.render();
    this.onInitialize();
  }

  /**
   * Translate a key using uiManager, falling back to component defaults.
   * Reads uiManager at call time (not at construction time) so it works
   * even when uiManager is assigned after construction.
   */
  t(key: string, params?: TranslationParams): string {
    if (this.uiManager && typeof this.uiManager.t === 'function') {
      const result = this.uiManager.t(key, params);
      if (result) return result;
    }

    let text = this.getTranslationFallbacks()[key] || key;

    // Parameter interpolation: replace {{param}} placeholders
    if (params) {
      for (const [param, value] of Object.entries(params)) {
        text = text.split(`{{${param}}}`).join(String(value));
      }
    }

    return text;
  }

  /**
   * Override in subclasses to provide the default CSS class name.
   */
  protected abstract getDefaultCssClass(): string;

  /**
   * Override in subclasses to provide fallback translations.
   */
  protected abstract getTranslationFallbacks(): Record<string, string>;

  /**
   * Override in subclasses to render the component HTML.
   */
  protected abstract render(): void;

  /**
   * Called after initialize(). Override to attach event listeners,
   * subscribe to stores, etc.
   */
  protected onInitialize(): void {
    // Default no-op; subclasses override as needed
  }

  /**
   * Update UI text after a language change.
   */
  updateUILanguage(): void {
    this.render();
  }

  /**
   * Clean up the component. Override to add store unsubscription, event
   * listener removal, etc. Always call super.destroy().
   */
  destroy(): void {
    if (this.container) {
      this.container.innerHTML = '';
    }
  }
}
